import React, { useState, useEffect } from 'react';
import { 
  FileCheck, 
  Send, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  MessageSquare,
  ArrowRight,
  Users,
  Scale,
  UserCheck,
  Gavel,
  BadgeCheck,
  Globe,
  AlertTriangle,
  Plus,
  Filter,
  RefreshCw,
  Eye,
  Edit3,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { apiFetch } from '../services/api_service';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badge colors
const statusConfig = {
  draft: { color: 'bg-slate-100 text-slate-700', icon: Edit3, label: 'Draft' },
  pending_review: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Pending Review' },
  internal_review: { color: 'bg-blue-100 text-blue-700', icon: Users, label: 'Internal Review' },
  legal_review: { color: 'bg-purple-100 text-purple-700', icon: Scale, label: 'Legal Review' },
  auditor_review: { color: 'bg-indigo-100 text-indigo-700', icon: UserCheck, label: 'Auditor Review' },
  board_approval: { color: 'bg-orange-100 text-orange-700', icon: Gavel, label: 'Board Approval' },
  approved: { color: 'bg-emerald-100 text-emerald-700', icon: BadgeCheck, label: 'Approved' },
  published: { color: 'bg-green-100 text-green-700', icon: Globe, label: 'Published' },
  rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rejected' }
};

// Workflow Stages Visualization
const WorkflowStages = ({ currentStage, stages }) => {
  const stageIndex = stages.findIndex(s => s === currentStage);
  
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-2">
      {stages.map((stage, idx) => {
        const config = statusConfig[stage] || { color: 'bg-slate-100 text-slate-700', icon: Clock };
        const Icon = config.icon;
        const isActive = stage === currentStage;
        const isCompleted = idx < stageIndex;
        
        return (
          <React.Fragment key={stage}>
            <div 
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap
                ${isActive ? config.color : isCompleted ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-50 text-slate-400'}`}
            >
              {isCompleted ? <CheckCircle2 size={12} /> : <Icon size={12} />}
              <span className="hidden sm:inline">{config.label}</span>
            </div>
            {idx < stages.length - 1 && (
              <ArrowRight size={14} className={isCompleted ? 'text-emerald-400' : 'text-slate-300'} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Report Card Component
const ReportCard = ({ report, onSelect, isSelected }) => {
  const config = statusConfig[report.status] || statusConfig.draft;
  const Icon = config.icon;
  
  return (
    <div 
      onClick={() => onSelect(report)}
      data-testid={`report-card-${report.report_id}`}
      className={`p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md
        ${isSelected ? 'border-emerald-500 bg-emerald-50/50 shadow-md' : 'border-slate-200 bg-white hover:border-emerald-300'}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-slate-900">{report.title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{report.report_type} • {report.reporting_period}</p>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
          <Icon size={12} />
          {config.label}
        </span>
      </div>
      
      <div className="text-xs text-slate-500 space-y-1">
        <div className="flex items-center gap-2">
          <Clock size={12} />
          <span>Updated: {new Date(report.updated_at).toLocaleDateString()}</span>
        </div>
        {report.current_stage_entered_at && (
          <div className="flex items-center gap-2">
            <Users size={12} />
            <span>In stage since: {new Date(report.current_stage_entered_at).toLocaleDateString()}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Create Report Modal
const CreateReportModal = ({ isOpen, onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    title: '',
    report_type: 'annual_sustainability',
    reporting_period: new Date().getFullYear().toString()
  });
  const [loading, setLoading] = useState(false);

  const reportTypes = [
    { value: 'annual_sustainability', label: 'Annual Sustainability Report' },
    { value: 'csrd_report', label: 'CSRD Compliance Report' },
    { value: 'tcfd_report', label: 'TCFD Climate Report' },
    { value: 'quarterly_update', label: 'Quarterly ESG Update' },
    { value: 'impact_report', label: 'Impact Report' }
  ];

  const handleSubmit = async () => {
    if (!formData.title) return;
    setLoading(true);
    try {
      await onCreate(formData);
      setFormData({ title: '', report_type: 'annual_sustainability', reporting_period: new Date().getFullYear().toString() });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" data-testid="create-report-modal">
        <h2 className="text-xl font-bold text-slate-900 mb-4">Create New Report</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Report Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="e.g., 2024 Annual Sustainability Report"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              data-testid="report-title-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Report Type</label>
            <select
              value={formData.report_type}
              onChange={(e) => setFormData({...formData, report_type: e.target.value})}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              data-testid="report-type-select"
            >
              {reportTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Reporting Period</label>
            <input
              type="text"
              value={formData.reporting_period}
              onChange={(e) => setFormData({...formData, reporting_period: e.target.value})}
              placeholder="e.g., 2024 or FY2024"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              data-testid="reporting-period-input"
            />
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !formData.title}
            className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
            data-testid="create-report-submit"
          >
            {loading ? 'Creating...' : 'Create Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Report Detail Panel
const ReportDetailPanel = ({ report, stages, onAction, onAddComment }) => {
  const [comment, setComment] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [showComments, setShowComments] = useState(true);
  const [comments, setComments] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);

  const config = statusConfig[report.status] || statusConfig.draft;
  const StatusIcon = config.icon;

  useEffect(() => {
    loadComments();
    loadAuditTrail();
  }, [report.report_id]);

  const loadComments = async () => {
    const data = await apiFetch(`${API}/report-workflow/reports/${report.report_id}/comments`);
    if (data?._error) {
      console.error('Failed to load comments:', data.message);
      return;
    }
    setComments(data.comments || []);
  };

  const loadAuditTrail = async () => {
    const data = await apiFetch(`${API}/report-workflow/reports/${report.report_id}/audit-trail`);
    if (data?._error) {
      console.error('Failed to load audit trail:', data.message);
      return;
    }
    setAuditTrail(data.audit_trail || []);
  };

  const handleAction = async (action, data = {}) => {
    setActionLoading(action);
    try {
      await onAction(action, data);
      await loadAuditTrail();
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    const data = await apiFetch(`${API}/report-workflow/reports/${report.report_id}/comments`, {
      method: 'POST',
      body: { comment, comment_type: 'general' }
    });
    if (data?._error) {
      console.error('Failed to add comment:', data.message);
      return;
    }
    setComment('');
    await loadComments();
  };

  // Determine available actions based on status
  const getAvailableActions = () => {
    const actions = [];
    switch (report.status) {
      case 'draft':
        actions.push({ action: 'submit', label: 'Submit for Review', target: 'internal_review', icon: Send, color: 'bg-blue-600 hover:bg-blue-700' });
        break;
      case 'internal_review':
        actions.push({ action: 'approve', label: 'Approve (Internal)', role: 'internal_reviewer', icon: CheckCircle2, color: 'bg-emerald-600 hover:bg-emerald-700' });
        actions.push({ action: 'reject', label: 'Request Revision', icon: XCircle, color: 'bg-red-600 hover:bg-red-700' });
        break;
      case 'legal_review':
        actions.push({ action: 'approve', label: 'Approve (Legal)', role: 'legal_counsel', icon: CheckCircle2, color: 'bg-emerald-600 hover:bg-emerald-700' });
        actions.push({ action: 'reject', label: 'Request Revision', icon: XCircle, color: 'bg-red-600 hover:bg-red-700' });
        break;
      case 'auditor_review':
        actions.push({ action: 'approve', label: 'Approve (Auditor)', role: 'external_auditor', icon: CheckCircle2, color: 'bg-emerald-600 hover:bg-emerald-700' });
        actions.push({ action: 'reject', label: 'Request Revision', icon: XCircle, color: 'bg-red-600 hover:bg-red-700' });
        break;
      case 'board_approval':
        actions.push({ action: 'approve', label: 'Board Approval', role: 'board_member', icon: BadgeCheck, color: 'bg-emerald-600 hover:bg-emerald-700' });
        actions.push({ action: 'reject', label: 'Request Revision', icon: XCircle, color: 'bg-red-600 hover:bg-red-700' });
        break;
      case 'approved':
        actions.push({ action: 'publish', label: 'Publish Report', icon: Globe, color: 'bg-green-600 hover:bg-green-700' });
        break;
      default:
        break;
    }
    return actions;
  };

  const availableActions = getAvailableActions();

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden h-full flex flex-col" data-testid="report-detail-panel">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-start justify-between mb-2">
          <h2 className="text-lg font-bold text-slate-900">{report.title}</h2>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
            <StatusIcon size={14} />
            {config.label}
          </span>
        </div>
        <p className="text-sm text-slate-600">{report.report_type} • {report.reporting_period}</p>
      </div>

      {/* Workflow Progress */}
      <div className="p-4 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Workflow Progress</h3>
        <WorkflowStages currentStage={report.status} stages={stages} />
      </div>

      {/* Actions */}
      {availableActions.length > 0 && (
        <div className="p-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Available Actions</h3>
          <div className="flex flex-wrap gap-2">
            {availableActions.map(act => {
              const Icon = act.icon;
              return (
                <button
                  key={act.action}
                  onClick={() => handleAction(act.action, { target_status: act.target, reviewer_role: act.role })}
                  disabled={actionLoading === act.action}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-sm font-medium ${act.color} disabled:opacity-50 transition-colors`}
                  data-testid={`action-${act.action}`}
                >
                  <Icon size={14} />
                  {actionLoading === act.action ? 'Processing...' : act.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Comments Section */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <button 
          onClick={() => setShowComments(!showComments)}
          className="w-full p-3 border-b border-slate-200 flex items-center justify-between text-left hover:bg-slate-50"
        >
          <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <MessageSquare size={16} />
            Comments ({comments.length})
          </span>
          {showComments ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        
        {showComments && (
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {comments.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No comments yet</p>
            ) : (
              comments.map((c, idx) => (
                <div key={idx} className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm text-slate-800">{c.comment}</p>
                  <p className="text-xs text-slate-500 mt-1">{new Date(c.created_at).toLocaleString()}</p>
                </div>
              ))
            )}
          </div>
        )}

        {/* Add Comment */}
        <div className="p-3 border-t border-slate-200">
          <div className="flex gap-2">
            <input
              type="text"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment..."
              className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              data-testid="comment-input"
            />
            <button
              onClick={handleAddComment}
              disabled={!comment.trim()}
              className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              data-testid="add-comment-btn"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Audit Trail */}
      <div className="border-t border-slate-200">
        <button 
          className="w-full p-3 flex items-center justify-between text-left hover:bg-slate-50"
          onClick={() => {}}
        >
          <span className="text-sm font-semibold text-slate-700">Audit Trail ({auditTrail.length})</span>
        </button>
        <div className="max-h-48 overflow-y-auto px-3 pb-3">
          {auditTrail.slice(0, 5).map((entry, idx) => (
            <div key={idx} className="flex items-start gap-2 py-2 border-b border-slate-100 last:border-0">
              <div className={`w-2 h-2 rounded-full mt-1.5 ${
                entry.action === 'approve' ? 'bg-emerald-500' : 
                entry.action === 'reject' ? 'bg-red-500' : 'bg-blue-500'
              }`} />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-800 truncate">{entry.action}: {entry.from_status} → {entry.to_status}</p>
                <p className="text-[10px] text-slate-500">{new Date(entry.timestamp).toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main Component
const ReportApprovalWorkflow = () => {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [workflowStages, setWorkflowStages] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);

    // Load workflow stages first (public endpoint)
    const stagesData = await apiFetch(`${API}/report-workflow/stages`);
    if (stagesData?._error) {
      console.error('Failed to load data:', stagesData.message);
      setLoading(false);
      return;
    }
    setWorkflowStages(stagesData.stages || []);

    // Load user-specific data
    const [reportsData, summaryData] = await Promise.all([
      apiFetch(`${API}/report-workflow/reports`),
      apiFetch(`${API}/report-workflow/summary`)
    ]);
    if (reportsData?._error || summaryData?._error) {
      console.log('User reports not loaded');
      setReports([]);
      setSummary({ by_status: {} });
    } else {
      setReports(reportsData || []);
      setSummary(summaryData);
    }
    setLoading(false);
  };

  const createReport = async (data) => {
    const result = await apiFetch(`${API}/report-workflow/reports`, { method: 'POST', body: data });
    if (result?._error) {
      throw new Error(result.message);
    }
    await loadData();
  };

  const handleAction = async (action, data) => {
    if (!selectedReport) return;

    let res;
    switch (action) {
      case 'submit':
        res = await apiFetch(`${API}/report-workflow/reports/${selectedReport.report_id}/submit`,
          { method: 'POST', body: { target_status: data.target_status || 'internal_review' } });
        break;
      case 'approve':
        res = await apiFetch(`${API}/report-workflow/reports/${selectedReport.report_id}/approve`,
          { method: 'POST', body: { reviewer_role: data.reviewer_role || 'internal_reviewer' } });
        break;
      case 'reject':
        res = await apiFetch(`${API}/report-workflow/reports/${selectedReport.report_id}/reject`,
          { method: 'POST', body: { reviewer_role: data.reviewer_role || 'reviewer', reason: 'Revision required' } });
        break;
      case 'publish':
        res = await apiFetch(`${API}/report-workflow/reports/${selectedReport.report_id}/publish`, { method: 'POST', body: {} });
        break;
      default:
        break;
    }
    if (res?._error) {
      console.error('Action failed:', res.message);
      return;
    }
    await loadData();
    // Refresh selected report
    const updatedReports = await apiFetch(`${API}/report-workflow/reports`);
    if (updatedReports?._error) {
      console.error('Action failed:', updatedReports.message);
      return;
    }
    const updated = updatedReports.find(r => r.report_id === selectedReport.report_id);
    if (updated) setSelectedReport(updated);
  };

  const filteredReports = statusFilter === 'all' 
    ? reports 
    : reports.filter(r => r.status === statusFilter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="loading-state">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="report-approval-workflow">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <FileCheck className="text-emerald-600" size={28} />
              Report Approval Workflow
            </h1>
            <p className="text-slate-600 mt-1">Multi-stage approval process with complete audit trail</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            data-testid="create-report-btn"
          >
            <Plus size={18} />
            New Report
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {Object.entries(summary.by_status || {}).map(([status, count]) => {
            const config = statusConfig[status] || statusConfig.draft;
            const Icon = config.icon;
            return (
              <div 
                key={status}
                onClick={() => setStatusFilter(status === statusFilter ? 'all' : status)}
                className={`p-3 rounded-xl border cursor-pointer transition-all
                  ${status === statusFilter ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white hover:border-emerald-300'}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon size={14} className="text-slate-500" />
                  <span className="text-xs text-slate-500 truncate">{config.label}</span>
                </div>
                <p className="text-xl font-bold text-slate-900">{count}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Reports List */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-slate-900">Reports ({filteredReports.length})</h2>
            <button onClick={loadData} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <RefreshCw size={16} className="text-slate-500" />
            </button>
          </div>
          
          {filteredReports.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-300">
              <FileCheck className="mx-auto h-12 w-12 text-slate-400 mb-3" />
              <p className="text-slate-500">No reports found</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-3 text-emerald-600 hover:text-emerald-700 text-sm font-medium"
              >
                Create your first report
              </button>
            </div>
          ) : (
            <div className="space-y-3 max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
              {filteredReports.map(report => (
                <ReportCard
                  key={report.report_id}
                  report={report}
                  onSelect={setSelectedReport}
                  isSelected={selectedReport?.report_id === report.report_id}
                />
              ))}
            </div>
          )}
        </div>

        {/* Report Detail */}
        <div className="lg:col-span-3">
          {selectedReport ? (
            <ReportDetailPanel
              report={selectedReport}
              stages={workflowStages}
              onAction={handleAction}
            />
          ) : (
            <div className="bg-slate-50 rounded-xl border border-dashed border-slate-300 h-96 flex items-center justify-center">
              <div className="text-center">
                <Eye className="mx-auto h-12 w-12 text-slate-400 mb-3" />
                <p className="text-slate-500">Select a report to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <CreateReportModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={createReport}
      />
    </div>
  );
};

export default ReportApprovalWorkflow;
